import React from 'react';
import styled, { keyframes } from 'styled-components';
import Button from '@mui/material/Button';
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Link
} from "react-router-dom";

import './App.css';
import { COLOR_PALETTE } from "./common/Constants"
import { LogoArt } from "./common/LogoArt"
import Navbar from './common/Navbar.js';
import { Particles } from './common/Particles';
import { SoundToggle } from './common/SoundToggle';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const MainContent = styled.div`
  margin: 0 auto;
  text-align: left;
  max-width: 1100px;
  min-height: calc(100vh - 64px);
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 40px;
  align-items: center;
  padding: 20px 50px;
  animation: ${fadeIn} 1s ease-out;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
    padding: 20px;
    min-height: auto;
  }
`;

const PageContent = styled.div`
  margin: 0 auto;
  text-align: left;
  max-width: 600px;
  margin-top: 80px;
  padding: 50px;
  line-height: 1.8;
  animation: ${fadeIn} 1s ease-out;
`;

const TaglineHeader = styled.h2`
  color: ${COLOR_PALETTE.secondary};
  margin: 0 0 24px 0;
  font-size: 36px;
  line-height: 1.2;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 500;
  letter-spacing: -0.02em;
`

const SectionHeader = styled.h3`
  color: ${COLOR_PALETTE.secondary};
  margin: 40px 0 16px 0;
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 500;
  font-size: 22px;
  letter-spacing: -0.01em;
`

const Paragraph = styled.p`
  margin: 0 0 16px 0;
  color: ${COLOR_PALETTE.text};
  font-size: 15px;
  line-height: 1.7;
  font-weight: 300;
`

const Summary = styled.div`
  min-width: 280px;
`

const ArtPanel = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 40px;

  @media (max-width: 768px) {
    order: -1;
    margin-bottom: 30px;
  }
`

const ContactInfo = styled.div`
  margin-top: 30px;
  line-height: 1.8;
  color: ${COLOR_PALETTE.text};
`

const StyledButton = styled(Button)`
  && {
    background: transparent;
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: ${COLOR_PALETTE.text};
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 13px;
    letter-spacing: 0.05em;
    padding: 10px 24px;
    border-radius: 0;
    transition: all 0.3s ease;

    &:hover {
      border-color: rgba(255, 255, 255, 0.8);
      background: rgba(255, 255, 255, 0.05);
    }
  }
`

function App() {
  return (
    <Router>
      <div>
        <Particles />
        <Navbar />
        <SoundToggle />
        <Switch>
          <Route exact path="/">
            <Home />
          </Route>
          <Route path="/about">
            <About />
          </Route>
          <Route path="/contact">
            <Contact />
          </Route>
        </Switch>
      </div>
    </Router>
  );
}

function Home() {
  return (
    <MainContent>
      <Summary>
        <TaglineHeader>
          Contemplative Learning Technology
        </TaglineHeader>
        <Paragraph>
          We build interactive tools that help people explore wisdom traditions and understand themselves.
        </Paragraph>
        <Paragraph>
          Modern learning methods - adaptive, data-driven, engaging - applied to timeless contemplative content.
        </Paragraph>
        <Link to='/about' style={{ textDecoration: 'none', display: 'inline-block', marginTop: '8px' }}>
          <StyledButton variant="contained">
            Learn More
          </StyledButton>
        </Link>
      </Summary>
      <ArtPanel>
        <LogoArt />
      </ArtPanel>
    </MainContent>
  );
}

function About() {
  return (
    <PageContent>
      <TaglineHeader>About Improvta</TaglineHeader>
      <Paragraph>
        Improvta - from "improv data" - began with a simple idea: storytelling with data.
        We believe that the most meaningful data isn't always numbers on a dashboard.
        Sometimes it's the texts, traditions, and frameworks that have helped humans
        understand themselves for centuries.
      </Paragraph>

      <SectionHeader>Our Mission</SectionHeader>
      <Paragraph>
        We are a small team dedicated to uplifting humanity through tools for
        self-understanding. Our apps bring modern learning technology to contemplative
        content - making wisdom traditions accessible, engaging, and personal.
      </Paragraph>

      <SectionHeader>Our Approach</SectionHeader>
      <Paragraph>
        We design for warmth and accessibility first, allowing users to go as deep as
        they choose. No academic gatekeeping, no harsh judgments - just thoughtful tools
        that meet seekers where they are.
      </Paragraph>

      <Link to='/contact' style={{ textDecoration: 'none', marginTop: '24px', display: 'inline-block' }}>
        <StyledButton variant="contained">
          Get in Touch
        </StyledButton>
      </Link>
    </PageContent>
  );
}

function Contact() {
  return (
    <PageContent>
      <TaglineHeader>Contact Us</TaglineHeader>
      <Paragraph>
        We'd love to hear from you. Whether you have questions, feedback, or just want
        to connect, reach out anytime.
      </Paragraph>

      <ContactInfo>
        <strong>Email</strong><br/>
        <a href="mailto:hello@improvta.com" style={{ color: 'rgba(255,255,255,0.7)' }}>
          hello@improvta.com
        </a>
      </ContactInfo>
    </PageContent>
  );
}

export default App;
